/**
 * ApplicationAiResources
 *
 * Renders AI resources for an application: use cases, agents, gen AI models,
 * and MCP servers.
 *
 * Used in two contexts:
 *  - systemType=AiSystem: rendered directly as the Resources tab content
 *    (no sub-tabs — all resources on an AI System are AI resources)
 *  - systemType=App: rendered as the "AI" sub-tab within the existing
 *    Resources tab alongside Cloud Accounts / Cloud Foundry / Devices
 *
 * Data is provided by useAiResources(), which stubs against app 117410 while
 * USE_STUBBED_AI_RESOURCES=true. Flip the flag when Flow / Snow are live in
 * the Galileo supergraph (fusionrage).
 */
import { useState, useContext } from 'react';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';
import {
  DevHubAlert,
  DevHubBox,
  DevHubButton,
  DevHubChip,
  DevHubGrid,
  DevHubPaper,
  DevHubTooltip,
  DevHubTypography,
} from '@devxdiscover/devhub-ui';
import { Progress } from '@backstage/core-components';
import {
  DataTable,
  DataTableExpandedRows,
  DataTableValueArray,
} from 'primereact/datatable';
import { Column } from 'primereact/column';
import { useAppId, SystemContext } from '../../../contexts/SystemContext';
import { useAiResources } from '../../ai-system/useAiResources';
import { UnlinkUseCaseDialog } from '../../ai-system/UnlinkUseCaseDialog';

// TODO: move to Vault / app config before production.
const SERVICENOW_USE_CASE_BASE_URL =
  'https://comcastsec.service-now.com/now/nav/ui/classic/params/target/x_mcse_aim_ai_intake_request.do%3Fsys_id%3D';
import {
  AiAgent,
  AiUseCase,
  GenAiModel,
  McpServer,
} from '../../ai-system/types';

// ── Status chip helpers ──────────────────────────────────────────────────────

const INTAKE_STATUS_COLORS: Record<
  string,
  'primary' | 'default' | 'secondary'
> = {
  APPROVED: 'primary',
  APPROVED_WITH_CONDITIONS: 'default',
  AUTO_APPROVED: 'primary',
};

const DEPLOYMENT_STATUS_COLORS: Record<
  string,
  'primary' | 'default' | 'secondary'
> = {
  ACTIVE: 'primary',
  INACTIVE: 'default',
  DEPRECATED: 'secondary',
};

// ── Section wrapper ──────────────────────────────────────────────────────────

const SectionHeading = ({ label }: { label: string }) => (
  <DevHubBox mt={3} mb={1}>
    <DevHubTypography variant="h6">{label}</DevHubTypography>
  </DevHubBox>
);

// ── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Formats an ISO date string as MM/DD/YYYY, h:mm AM/PM.
 * Returns '—' for null/undefined input; returns null for a present but unparseable value.
 * @param value - ISO date string to format
 */
const formatDateTime = (value: string | undefined | null): string | null => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: 'numeric',
    minute: '2-digit',
  });
};

/**
 * Renders a formatted date, or a tooltip-wrapped '—' when the raw value cannot be parsed.
 * @param raw - ISO date string to render
 */
const dateValue = (raw: string | undefined | null): React.ReactNode => {
  const formatted = formatDateTime(raw);
  if (formatted === null) {
    return (
      <DevHubTooltip title={`Unable to parse as a valid date: "${raw}"`}>
        <span
          style={{ cursor: 'help', borderBottom: '1px dotted currentColor' }}
        >
          —
        </span>
      </DevHubTooltip>
    );
  }
  return formatted;
};

/** Renders a label/value pair in a 2-up grid layout used by expansion rows.
 * @param root0 - Component props
 * @param root0.label - The field label to display
 * @param root0.value - The field value to display
 */
const ExpField = ({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) => (
  <>
    <DevHubGrid item xs={2}>
      <span style={{ fontWeight: 'bold' }}>{label}</span>
    </DevHubGrid>
    <DevHubGrid item xs={4}>
      <DevHubTypography variant="body2">{value ?? '—'}</DevHubTypography>
    </DevHubGrid>
  </>
);

// ── Use Cases table ──────────────────────────────────────────────────────────

const UseCasesSection = ({
  useCases,
  applicationId,
  applicationName,
  onUnlinkSuccess,
}: {
  useCases: AiUseCase[];
  applicationId: number;
  applicationName?: string;
  onUnlinkSuccess: () => void;
}) => {
  const [unlinkRow, setUnlinkRow] = useState<AiUseCase | null>(null);
  const [expandedRows, setExpandedRows] = useState<
    DataTableExpandedRows | DataTableValueArray | undefined
  >(undefined);

  if (useCases.length === 0) return null;

  const rowExpansionTemplate = (row: AiUseCase) => (
    <DevHubGrid container spacing={1} style={{ padding: '8px 16px 12px' }}>
      <ExpField label="Created At" value={dateValue(row.createdAt)} />
      <ExpField label="Intended Users" value={row.intendedUsers} />
      <ExpField
        label="Last Updated Date"
        value={dateValue(row.lastUpdatedDate)}
      />
      <ExpField label="Model Name" value={row.modelName} />
      <ExpField label="Platform Name" value={row.platformName} />
      <ExpField
        label="Priority"
        value={row.priority !== undefined ? String(row.priority) : undefined}
      />
      <ExpField
        label="Requested By"
        value={row.requestedBy?.displayName ?? row.requestedBy?.login}
      />
      <ExpField label="Target Environment" value={row.targetEnvironment} />
      <ExpField label="VP Decision" value={row.vpDecision} />
    </DevHubGrid>
  );

  return (
    <>
      <SectionHeading label="Use Cases" />
      <DevHubPaper>
        <DataTable
          value={useCases}
          dataKey="useCaseId"
          responsiveLayout="scroll"
          rowHover
          sortField="useCaseId"
          sortOrder={1}
          expandedRows={expandedRows}
          onRowToggle={e => setExpandedRows(e.data)}
          rowExpansionTemplate={rowExpansionTemplate}
        >
          <Column expander style={{ width: '3rem' }} />
          <Column
            field="useCaseId"
            header="Use Case ID"
            style={{ width: '140px' }}
            body={(row: AiUseCase) =>
              row.useCaseSysId ? (
                <a
                  href={`${SERVICENOW_USE_CASE_BASE_URL}${row.useCaseSysId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <DevHubTypography variant="body2">
                    {row.useCaseId}
                  </DevHubTypography>
                </a>
              ) : (
                <DevHubTypography variant="body2">
                  {row.useCaseId}
                </DevHubTypography>
              )
            }
          />
          <Column
            field="title"
            header="Title"
            style={{ width: 'auto' }}
            body={(row: AiUseCase) => (
              <DevHubTypography variant="body2">
                {row.title ?? row.useCaseId}
              </DevHubTypography>
            )}
          />
          <Column
            field="businessUseCase"
            header="Business Use Case"
            style={{ width: 'auto' }}
            body={(row: AiUseCase) => (
              <DevHubTypography variant="body2" opacity="hazy">
                {row.businessUseCase ?? '—'}
              </DevHubTypography>
            )}
          />
          <Column
            header="Status"
            style={{ width: '200px' }}
            body={(row: AiUseCase) => {
              const key = row.overallIntakeStatus ?? row.status;
              if (!key) {
                return (
                  <DevHubTooltip title="Status information is not yet available for this use case.">
                    <span>
                      <DevHubTypography variant="body2" opacity="hazy">
                        —
                      </DevHubTypography>
                    </span>
                  </DevHubTooltip>
                );
              }
              const chip = (
                <DevHubChip
                  size="small"
                  label={key}
                  color={INTAKE_STATUS_COLORS[key] ?? 'default'}
                />
              );
              if (key === 'UNEXPECTED_VALUE') {
                return (
                  <DevHubTooltip title="This use case has a status value not yet recognised by this system. Check CaaS directly for the current status.">
                    <span>{chip}</span>
                  </DevHubTooltip>
                );
              }
              return chip;
            }}
          />
          <Column
            field="riskAssessmentScore"
            header="Risk"
            style={{ width: '100px' }}
            body={(row: AiUseCase) => (
              <DevHubTypography variant="body2">
                {row.riskAssessmentScore ?? '—'}
              </DevHubTypography>
            )}
          />
          <Column
            header=""
            style={{ width: '90px' }}
            body={(row: AiUseCase) => (
              <DevHubButton
                size="small"
                variant="outlined"
                onClick={() => setUnlinkRow(row)}
              >
                Unlink
              </DevHubButton>
            )}
          />
        </DataTable>
      </DevHubPaper>
      {unlinkRow && (
        <UnlinkUseCaseDialog
          useCaseId={unlinkRow.useCaseId}
          applicationId={applicationId}
          applicationName={applicationName}
          open
          onClose={() => setUnlinkRow(null)}
          onSuccess={() => {
            setUnlinkRow(null);
            onUnlinkSuccess();
          }}
        />
      )}
    </>
  );
};

// ── Agents table ─────────────────────────────────────────────────────────────

const AgentsSection = ({ agents }: { agents: AiAgent[] }) => {
  const [expandedRows, setExpandedRows] = useState<
    DataTableExpandedRows | DataTableValueArray | undefined
  >(undefined);

  if (agents.length === 0) return null;

  const rowExpansionTemplate = (row: AiAgent) => (
    <DevHubGrid container spacing={1} style={{ padding: '8px 16px 12px' }}>
      <ExpField label="Agent ID" value={row.agentId} />
      <ExpField label="Auth Method" value={row.authMethod} />
      <ExpField label="Bryter Intake ID" value={row.bryterIntakeId} />
      <ExpField label="Create Date" value={dateValue(row.createDate)} />
      <ExpField
        label="Last LoA Update Date"
        value={dateValue(row.lastLoaUpdateDate)}
      />
      <ExpField
        label="Last Updated Date"
        value={dateValue(row.lastUpdatedDate)}
      />
      <ExpField
        label="Log Enabled"
        value={
          row.logEnabled !== undefined ? String(row.logEnabled) : undefined
        }
      />
      <ExpField
        label="Monitoring Enabled"
        value={
          row.monitoringEnabled !== undefined
            ? String(row.monitoringEnabled)
            : undefined
        }
      />
      <ExpField label="Vendor TPE ID" value={row.vendorTpeId} />
    </DevHubGrid>
  );

  return (
    <>
      <SectionHeading label="Agents" />
      <DevHubPaper>
        <DataTable
          value={agents}
          dataKey="agentId"
          responsiveLayout="scroll"
          rowHover
          expandedRows={expandedRows}
          onRowToggle={e => setExpandedRows(e.data)}
          rowExpansionTemplate={rowExpansionTemplate}
        >
          <Column expander style={{ width: '40px' }} />
          <Column
            field="name"
            header="Agent"
            style={{ width: 'auto' }}
            body={(row: AiAgent) => (
              <DevHubTypography variant="body2" weight="bold">
                {row.name}
              </DevHubTypography>
            )}
          />
          <Column
            field="agentType"
            header="Type"
            style={{ width: '110px' }}
            body={(row: AiAgent) => (
              <DevHubTypography variant="body2">
                {row.agentType ?? '—'}
              </DevHubTypography>
            )}
          />
          <Column
            field="levelOfAgency"
            header="Agency Level"
            style={{ width: '120px' }}
            body={(row: AiAgent) => (
              <DevHubTypography variant="body2">
                {row.levelOfAgency ?? '—'}
              </DevHubTypography>
            )}
          />
          <Column
            field="interactionSurface"
            header="Interaction Surface"
            style={{ width: '200px' }}
            body={(row: AiAgent) => (
              <DevHubTypography variant="body2">
                {row.interactionSurface ?? '—'}
              </DevHubTypography>
            )}
          />
          <Column
            header="Status"
            style={{ width: '120px' }}
            body={(row: AiAgent) => {
              const s = row.deploymentStatus ?? '';
              return (
                <DevHubChip
                  size="small"
                  label={s || '—'}
                  color={DEPLOYMENT_STATUS_COLORS[s] ?? 'default'}
                />
              );
            }}
          />
        </DataTable>
      </DevHubPaper>
    </>
  );
};

// ── Gen AI Models table ───────────────────────────────────────────────────────

const GenAiModelsSection = ({ models }: { models: GenAiModel[] }) => {
  const [expandedRows, setExpandedRows] = useState<
    DataTableExpandedRows | DataTableValueArray | undefined
  >(undefined);

  if (models.length === 0) return null;

  const rowExpansionTemplate = (row: GenAiModel) => (
    <DevHubGrid container spacing={1} style={{ padding: '8px 16px 12px' }}>
      <ExpField label="Cloud Or On-Prem" value={row.cloudOrOnPrem} />
      <ExpField label="Create Date" value={dateValue(row.createDate)} />
      <ExpField label="Family" value={row.family} />
      <ExpField
        label="Last Updated Date"
        value={dateValue(row.lastUpdatedDate)}
      />
      <ExpField label="Model ID" value={row.modelId} />
      <ExpField label="Vendor TPE ID" value={row.vendorTpeId} />
    </DevHubGrid>
  );

  return (
    <>
      <SectionHeading label="Gen AI Models" />
      <DevHubPaper>
        <DataTable
          value={models}
          dataKey="modelId"
          responsiveLayout="scroll"
          rowHover
          expandedRows={expandedRows}
          onRowToggle={e => setExpandedRows(e.data)}
          rowExpansionTemplate={rowExpansionTemplate}
        >
          <Column expander style={{ width: '40px' }} />
          <Column
            field="name"
            header="Model"
            style={{ width: 'auto' }}
            body={(row: GenAiModel) => (
              <DevHubTypography variant="body2" weight="bold">
                {row.name}
              </DevHubTypography>
            )}
          />
          <Column
            field="provider"
            header="Provider"
            style={{ width: '140px' }}
            body={(row: GenAiModel) => (
              <DevHubTypography variant="body2">
                {row.provider ?? '—'}
              </DevHubTypography>
            )}
          />
          <Column
            field="aiCapabilityType"
            header="Capability"
            style={{ width: '160px' }}
            body={(row: GenAiModel) => (
              <DevHubTypography variant="body2">
                {row.aiCapabilityType ?? '—'}
              </DevHubTypography>
            )}
          />
          <Column
            field="openSource"
            header="Open Source"
            style={{ width: '120px' }}
            body={(row: GenAiModel) => {
              const openSourceLabel = () => {
                if (row.openSource === undefined) return '—';
                return row.openSource ? 'Yes' : 'No';
              };
              return <DevHubChip size="small" label={openSourceLabel()} />;
            }}
          />
          <Column
            field="transparency"
            header="Transparency"
            style={{ width: '130px' }}
            body={(row: GenAiModel) => (
              <DevHubTypography variant="body2">
                {row.transparency ?? '—'}
              </DevHubTypography>
            )}
          />
          <Column
            header="Status"
            style={{ width: '120px' }}
            body={(row: GenAiModel) => {
              const s = row.deploymentStatus ?? '';
              return (
                <DevHubChip
                  size="small"
                  label={s || '—'}
                  color={DEPLOYMENT_STATUS_COLORS[s] ?? 'default'}
                />
              );
            }}
          />
        </DataTable>
      </DevHubPaper>
    </>
  );
};

// ── MCP Servers table ─────────────────────────────────────────────────────────

const McpServersSection = ({ servers }: { servers: McpServer[] }) => {
  const [expandedRows, setExpandedRows] = useState<
    DataTableExpandedRows | DataTableValueArray | undefined
  >(undefined);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const handleCopy = (serverId: string, url: string) => {
    navigator.clipboard
      .writeText(url)
      .then(() => {
        setCopiedId(serverId);
        setTimeout(() => setCopiedId(null), 2000);
      })
      .catch(() => {});
  };

  if (servers.length === 0) return null;

  const rowExpansionTemplate = (row: McpServer) => (
    <DevHubGrid container spacing={1} style={{ padding: '8px 16px 12px' }}>
      <ExpField label="Create Date" value={dateValue(row.createDate)} />
      <ExpField
        label="Last Updated Date"
        value={dateValue(row.lastUpdatedDate)}
      />
      <DevHubGrid item xs={2}>
        <span style={{ fontWeight: 'bold' }}>Remote URL</span>
      </DevHubGrid>
      <DevHubGrid item xs={4}>
        {row.remoteUrl ? (
          <DevHubBox display="flex" alignItems="center" style={{ gap: '6px' }}>
            <DevHubTypography variant="body2" opacity="hazy">
              {row.remoteUrl}
            </DevHubTypography>
            <DevHubTooltip
              title={copiedId === row.serverId ? 'Copied!' : 'Copy URL'}
            >
              <button
                type="button"
                aria-label="Copy URL"
                onClick={() => handleCopy(row.serverId, row.remoteUrl!)}
                style={{
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  background: 'none',
                  border: 'none',
                  padding: 0,
                }}
              >
                <FileCopyOutlinedIcon
                  style={{ fontSize: '14px', opacity: 0.6 }}
                />
              </button>
            </DevHubTooltip>
          </DevHubBox>
        ) : (
          <DevHubTypography variant="body2">—</DevHubTypography>
        )}
      </DevHubGrid>
      <ExpField label="Server ID" value={row.serverId} />
    </DevHubGrid>
  );

  return (
    <>
      <SectionHeading label="MCP Servers" />
      <DevHubPaper>
        <DataTable
          value={servers}
          dataKey="serverId"
          responsiveLayout="scroll"
          rowHover
          expandedRows={expandedRows}
          onRowToggle={e => setExpandedRows(e.data)}
          rowExpansionTemplate={rowExpansionTemplate}
        >
          <Column expander style={{ width: '40px' }} />
          <Column
            field="name"
            header="Server"
            style={{ width: 'auto' }}
            body={(row: McpServer) => (
              <>
                <DevHubTypography variant="body2" weight="bold">
                  {row.name}
                </DevHubTypography>
                {row.description && (
                  <DevHubTypography variant="caption" opacity="hazy">
                    {row.description}
                  </DevHubTypography>
                )}
              </>
            )}
          />
          <Column
            field="authMethod"
            header="Auth Method(s)"
            style={{ width: '200px' }}
            body={(row: McpServer) => {
              const methods = row.authMethod ?? [];
              if (methods.length === 0) {
                return <DevHubTypography variant="body2">—</DevHubTypography>;
              }
              return (
                <DevHubBox
                  display="flex"
                  style={{ gap: '4px', flexWrap: 'wrap' }}
                >
                  {methods.map(m => (
                    <DevHubChip key={m} size="small" label={m} />
                  ))}
                </DevHubBox>
              );
            }}
          />
          <Column
            field="license"
            header="License"
            style={{ width: '130px' }}
            body={(row: McpServer) => (
              <DevHubTypography variant="body2">
                {row.license ?? '—'}
              </DevHubTypography>
            )}
          />
          <Column
            field="dataClassification"
            header="Data Classification"
            style={{ width: '190px' }}
            body={(row: McpServer) => (
              <DevHubChip size="small" label={row.dataClassification ?? '—'} />
            )}
          />
        </DataTable>
      </DevHubPaper>
    </>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

/**
 * ApplicationAiResources renders AI resource sections for the current application.
 * Sections are conditionally shown — only sections with data are rendered.
 */
export const ApplicationAiResources = () => {
  const appId = useAppId();
  const { resources, loading, error, reload } = useAiResources(appId);
  const { app } = useContext(SystemContext);

  if (loading) return <Progress />;

  const hasAny =
    (resources?.aiUseCases?.length ?? 0) > 0 ||
    (resources?.aiAgents?.length ?? 0) > 0 ||
    (resources?.genAiModels?.length ?? 0) > 0 ||
    (resources?.mcpServers?.length ?? 0) > 0;

  return (
    <DevHubBox>
      {error && (
        <DevHubAlert severity="warning" style={{ marginBottom: '16px' }}>
          AI resource data is temporarily unavailable. Some information may be
          incomplete.
        </DevHubAlert>
      )}
      {!hasAny ? (
        <DevHubPaper>
          <DevHubBox padding="large" display="flex" justifyContent="center">
            <DevHubBox style={{ textAlign: 'center', maxWidth: '480px' }}>
              <DevHubTypography variant="h6" style={{ marginBottom: '8px' }}>
                No AI resources
              </DevHubTypography>
              <DevHubTypography variant="body2" opacity="hazy">
                No agents, models, MCP servers, or other AI resources have been
                registered for this application yet.
              </DevHubTypography>
            </DevHubBox>
          </DevHubBox>
        </DevHubPaper>
      ) : (
        <>
          <UseCasesSection
            useCases={resources?.aiUseCases ?? []}
            applicationId={appId}
            applicationName={app?.name}
            onUnlinkSuccess={reload}
          />
          <AgentsSection agents={resources?.aiAgents ?? []} />
          <GenAiModelsSection models={resources?.genAiModels ?? []} />
          <McpServersSection servers={resources?.mcpServers ?? []} />
        </>
      )}
    </DevHubBox>
  );
};
